import { Num, Plural, T, Var } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import type { LegPriceBreak } from '@erp/shared-types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CostSourceBadge } from './CostSourceBadge';
import {
  resolvePriceBreakNudge,
  type PriceBreakNudge,
} from './priceBreakNudge';

const CHIP_CLASS =
  'inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 tabular-nums';

function NudgeLabel({
  nudge,
  compact,
}: {
  nudge: PriceBreakNudge;
  compact: boolean;
}) {
  // The board's quantity column is narrow and the rows are dense, so the chip
  // there says only what it costs and what it returns. The tier and the
  // repriced unit cost are one hover away either way.
  if (compact) {
    return (
      <T context="replenishment price-break nudge, short form for a dense table cell: units to add and what the line saves by adding them">
        +<Num>{nudge.addUnits}</Num> saves{' '}
        <Var>{formatCurrency(nudge.savings)}</Var>
      </T>
    );
  }

  return (
    <T context="replenishment price-break nudge: units to add to reach the vendor's next price tier, and what the line saves there">
      +<Num>{nudge.addUnits}</Num>{' '}
      <Plural n={nudge.addUnits} one="unit" other="units" /> reaches{' '}
      <Var>{formatCurrency(nudge.nextUnitCost)}</Var>, saves{' '}
      <Var>{formatCurrency(nudge.savings)}</Var>
    </T>
  );
}

function NudgeTooltip({
  nudge,
  priceBreak,
  applies,
}: {
  nudge: PriceBreakNudge;
  priceBreak: LegPriceBreak;
  applies: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
        <T context="replenishment price-break tooltip heading">Price break</T>
      </div>
      {priceBreak.unitCost != null && (
        <div className="text-sm">
          {priceBreak.tier == null ? (
            <T context="replenishment price-break tooltip: the vendor price this line is getting today, when the tier number is unknown">
              This line is priced at{' '}
              <Var>{formatCurrency(priceBreak.unitCost)}</Var> per unit.
            </T>
          ) : (
            <T context="replenishment price-break tooltip: the numbered vendor tier this line reached and its unit price">
              This line reached tier <Num>{priceBreak.tier}</Num> at{' '}
              <Var>{formatCurrency(priceBreak.unitCost)}</Var> per unit.
            </T>
          )}
        </div>
      )}
      <div className="text-sm">
        <T context="replenishment price-break tooltip: ordering up to the next tier quantity reprices every unit on the line">
          Ordering <Num>{nudge.targetQuantity}</Num> or more drops every unit on
          the line to <Var>{formatCurrency(nudge.nextUnitCost)}</Var>, worth{' '}
          <Var>{formatCurrency(nudge.savings)}</Var> on this line.
        </T>
      </div>
      {applies && (
        <div className="text-[10px] text-gray-400 italic pt-1 border-t border-gray-700">
          <T context="replenishment price-break tooltip hint: clicking the chip raises the order quantity">
            Click to raise the quantity
          </T>
        </div>
      )}
    </div>
  );
}

interface PriceBreakNudgeChipProps {
  nudge: PriceBreakNudge;
  priceBreak: LegPriceBreak;
  /**
   * Raise the line to `nudge.targetQuantity`. Omit to render the chip as a
   * static badge, which is what the read-only suggestion detail does.
   */
  onApply?: () => void;
  isApplying?: boolean;
  /** Shorten the label for a dense table cell; the tooltip is unchanged. */
  compact?: boolean;
  className?: string;
}

/**
 * The compact "+3 units reaches $6.00, saves $40" chip. Deliberately not a
 * dialog: buyers asked for the board to stop interrupting them, so the whole
 * offer including the tier explanation lives in a hover tooltip and applying
 * it is one click, which the server carries out at the rounded quantity and
 * the price the vendor charges there.
 */
export function PriceBreakNudgeChip({
  nudge,
  priceBreak,
  onApply,
  isApplying = false,
  compact = false,
  className,
}: PriceBreakNudgeChipProps) {
  const label = <NudgeLabel nudge={nudge} compact={compact} />;
  const tooltip = (
    <NudgeTooltip
      nudge={nudge}
      priceBreak={priceBreak}
      applies={onApply != null}
    />
  );

  const trigger =
    onApply == null ? (
      <span className={cn(CHIP_CLASS, className)}>{label}</span>
    ) : (
      <button
        type="button"
        disabled={isApplying}
        className={cn(
          CHIP_CLASS,
          'transition-colors hover:bg-emerald-100 disabled:opacity-50',
          className,
        )}
        onClick={(event) => {
          // The row underneath opens the suggestion detail; the chip is an
          // action of its own.
          event.stopPropagation();
          onApply();
        }}
      >
        {label}
      </button>
    );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Read-only price summary for one buy leg: what priced its unit cost (the
 * contract, or the price-break tier the engine reached) and, when the next
 * boundary is worth taking, the nudge chip beside it. Renders nothing for
 * legs the engine never priced.
 */
export function BuyLegPriceBreak({
  priceBreak,
  quantity,
}: {
  priceBreak: LegPriceBreak | null | undefined;
  quantity: number | null | undefined;
}) {
  const nudge = resolvePriceBreakNudge({ priceBreak, quantity });
  if (priceBreak == null) return null;

  return (
    <>
      {/* An agreement gets the modal's contract block instead of a badge. */}
      {priceBreak.costSource !== 'agreement' && (
        <CostSourceBadge priceBreak={priceBreak} className="text-xs" />
      )}
      {nudge != null && (
        <PriceBreakNudgeChip nudge={nudge} priceBreak={priceBreak} />
      )}
    </>
  );
}
